import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider } from 'react-hook-form';

import { ApplicationsExport } from '~domains/transactions/entities/application';

import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { useIsDesktop } from '~shared/lib/responsive';
import { DataTable } from '~shared/ui/data-table';
import { PeriodRangeFilter } from '~shared/ui/period-range-filter';
import { TableColumnsButton } from '~shared/ui/table-columns-button';
import { TableSortButton } from '~shared/ui/table-sort-button';

import { Modal } from '@breezeventures/ui-kit/modal';

import { ApplicationDetails } from './application-details/application-details';
import { ApplicationsFilters } from './filters/applications-filters';
import { ApplicationsFiltersMobile } from './filters/applications-filters-mobile';

import { COLUMN_OPTIONS, SORT_OPTIONS, columns } from '../lib/columns';
import {
  PERIOD_MAX_DATE,
  PERIOD_MIN_DATE,
  PERIOD_PRESETS,
} from '../lib/period-options';
import { TABLE_MIN_WIDTH } from '../lib/table-options';
import type { ApplicationsFiltersValue } from '../model/applications-filters';
import type { ApplicationsTableData } from '../model/applications-table-data';
import { useApplicationsTable } from '../model/use-applications-table';

type Props = {
  headerSlot?: HTMLElement | null;
};

export const ApplicationsTable = ({ headerSlot }: Props) => {
  const {
    methods,
    applications,
    total,
    isLoading,
    pagination,
    sorting,
    searchParams,
    hideMerchantFilter,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    onSortingChange,
    visibleColumns,
    onVisibleColumnsChange,
    columnVisibility,
    onPageChange,
    onPageSizeChange,
  } = useApplicationsTable();

  const isDesktop = useIsDesktop();
  const [detailsOpened, setDetailsOpened] = useState(false);
  const lastSelectedRef = useRef<ApplicationsTableData | null>(null);

  const handleRowClick = (item: ApplicationsTableData) => {
    lastSelectedRef.current = item;
    setDetailsOpened(true);
  };

  const periodFilter = (
    <PeriodRangeFilter<ApplicationsFiltersValue>
      name="period"
      presets={PERIOD_PRESETS}
      minDate={PERIOD_MIN_DATE}
      maxDate={PERIOD_MAX_DATE}
      onChange={applyFilters}
    />
  );

  const headerControls = (
    <>
      {periodFilter}
      <ApplicationsExport
        params={searchParams}
        disabled={applications.length === 0}
      />
    </>
  );

  const responsiveProps = isDesktop
    ? { minWidth: TABLE_MIN_WIDTH, sorting, onSortingChange }
    : { minWidth: 0, showRowChevron: true as const, columnVisibility };

  return (
    <FormProvider {...methods}>
      {isDesktop &&
        (headerSlot ? (
          createPortal(headerControls, headerSlot)
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {headerControls}
          </div>
        ))}

      <div
        className={`
          flex flex-col gap-4
          lg:gap-6
        `}
      >
        {isDesktop ? (
          <ApplicationsFilters
            hideMerchantFilter={hideMerchantFilter}
            onFiltersChange={applyFilters}
            onReset={resetFilters}
          />
        ) : (
          <div
            className={`
              -mx-4 flex
              [scrollbar-width:none]
              gap-2 overflow-x-auto px-4
              *:shrink-0
              [&::-webkit-scrollbar]:hidden
            `}
          >
            {periodFilter}

            <TableSortButton
              options={SORT_OPTIONS}
              value={sorting}
              onApply={onSortingChange}
            />

            <TableColumnsButton
              options={COLUMN_OPTIONS}
              value={visibleColumns}
              onApply={onVisibleColumnsChange}
            />

            <ApplicationsExport
              params={searchParams}
              disabled={applications.length === 0}
              size="s"
            />
          </div>
        )}

        <DataTable
          data={applications}
          columns={columns}
          getRowId={(row) => row.applicationId}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          emptyState="We didn't find anything"
          pagination={
            total > 0
              ? {
                  page: pagination.page,
                  pageSize: pagination.perPage,
                  itemsTotal: total,
                  onPageChange,
                  onPageSizeChange,
                }
              : undefined
          }
          {...responsiveProps}
        />
      </div>

      {!isDesktop && (
        <ApplicationsFiltersMobile
          hideMerchantFilter={hideMerchantFilter}
          activeCount={activeFiltersCount}
          onApply={applyFilters}
          onClear={clearSelectFilters}
        />
      )}

      <Modal
        opened={detailsOpened}
        onClose={() => setDetailsOpened(false)}
        title="Loan details"
        size="wide"
      >
        <ApplicationDetails
          application={nonNullableValue(lastSelectedRef.current)}
        />
      </Modal>
    </FormProvider>
  );
};
